type Rgb = readonly [number, number, number];

function rgb(value: string): Rgb | null {
  const match = value.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+))?\s*\)$/);
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
}

function luminance(color: Rgb): number {
  const channels = color.map(value => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

function contrast(first: Rgb, second: Rgb): number {
  const a = luminance(first), b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

function background(value: string, parent: Rgb): Rgb {
  const color = rgb(value);
  if (!color) return parent;
  const alpha = Number(value.match(/[,/]\s*([\d.]+)\s*\)$/)?.[1] ?? 1);
  const opacity = value.startsWith('rgba') || value.includes('/') ? alpha : 1;
  return [
    color[0] * opacity + parent[0] * (1 - opacity),
    color[1] * opacity + parent[1] * (1 - opacity),
    color[2] * opacity + parent[2] * (1 - opacity)
  ];
}

/** 원본 DOM을 읽기만 하며 표시 대상에 한정한 CSS를 반환한다. */
export function buildContentContrastRules(root: HTMLElement, selector: string, foregroundValue = 'hsl(var(--foreground))'): string {
  const canvas = getComputedStyle(root);
  const foreground = rgb(canvas.color);
  const canvasBackground = rgb(canvas.backgroundColor);
  if (!foreground || !canvasBackground) return '';
  const rules: string[] = [];
  const visit = (parent: Element, parentBackground: Rgb, path: string, inheritedCorrection: boolean) => {
    Array.from(parent.children).forEach((element, index) => {
      // 이미지·SVG·입력 컨트롤의 색상에는 관여하지 않는다.
      if (element.matches('img,svg,video,iframe,input,textarea,select,button,style,script')) return;
      const computed = getComputedStyle(element);
      const currentBackground = background(computed.backgroundColor, parentBackground);
      const color = rgb(computed.color);
      const selector = `${path} > :nth-child(${index + 1})`;
      const neutral = color && Math.max(...color) <= 128 && Math.max(...color) - Math.min(...color) <= 32;
      const correction = !!(neutral && color && contrast(color, currentBackground) < 4.5 && contrast(foreground, currentBackground) >= 4.5);
      if (correction) {
        rules.push(`${selector}{color:${foregroundValue} !important;}`);
      } else if (inheritedCorrection && color) {
        // 밝은 표 셀·배경 및 자손의 강조색은 원래 색상을 유지한다.
        rules.push(`${selector}{color:${computed.color} !important;}`);
      }
      visit(element, currentBackground, selector, correction);
    });
  };
  visit(root, canvasBackground, selector, false);
  return rules.join('\n');
}
